import { defaultAxiosInstance } from '../config/axiosConfig';

// Job statuses that will never change again — polling can stop on these.
export const TERMINAL_JOB_STATUSES = ['completed', 'completed_with_errors', 'failed', 'cancelled'];

export const isTerminalJobStatus = (status) => TERMINAL_JOB_STATUSES.includes(status);

/**
 * Service for the Loop Node batch jobs API (/batch-jobs).
 * All endpoints are scoped to the authenticated user via the x-api-key header.
 */
class BatchJobsService {
  constructor() {
    this.httpClient = defaultAxiosInstance;
  }

  /**
   * List the user's batch jobs, newest first.
   * @param {Object} options
   * @param {string} [options.status] - Optional status filter
   * @param {number} [options.page] - 1-based page number
   * @param {number} [options.pageSize] - Page size (max 100)
   * @returns {Promise<{total: number, page: number, page_size: number, jobs: Array}>}
   */
  async listJobs({ status = null, page = 1, pageSize = 20 } = {}) {
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    const response = await this.httpClient.get('/batch-jobs', { params });
    return response.data;
  }

  /**
   * Get a single batch job by id.
   * @param {string} jobId
   * @returns {Promise<Object>} The job object
   */
  async getJob(jobId) {
    const response = await this.httpClient.get(`/batch-jobs/${jobId}`);
    return response.data;
  }

  /**
   * Get paginated per-record items for a job.
   * @param {string} jobId
   * @param {Object} options
   * @param {string} [options.status] - pending | running | completed | failed
   * @param {number} [options.page] - 1-based page number
   * @param {number} [options.pageSize] - Page size (max 200)
   * @returns {Promise<{total: number, page: number, page_size: number, items: Array}>}
   */
  async getJobItems(jobId, { status = null, page = 1, pageSize = 50 } = {}) {
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    const response = await this.httpClient.get(`/batch-jobs/${jobId}/items`, { params });
    return response.data;
  }

  /** Cancel a non-terminal job at the next record boundary. */
  async cancelJob(jobId) {
    const response = await this.httpClient.post(`/batch-jobs/${jobId}/cancel`);
    return response.data;
  }

  /** Pause a pending/running job (current chunk drains, nothing new starts). */
  async pauseJob(jobId) {
    const response = await this.httpClient.post(`/batch-jobs/${jobId}/pause`);
    return response.data;
  }

  /** Resume a paused/failed job from where it stopped. */
  async resumeJob(jobId) {
    const response = await this.httpClient.post(`/batch-jobs/${jobId}/resume`);
    return response.data;
  }

  /** Reset failed items and re-run them (re-runs the sink afterwards). */
  async retryFailed(jobId) {
    const response = await this.httpClient.post(`/batch-jobs/${jobId}/retry-failed`);
    return response.data;
  }

  /**
   * Download the full result set as a file (json or csv).
   * Fetches as a blob (the endpoint requires the x-api-key header, so a plain
   * <a href> cannot be used) and triggers a browser download.
   * @param {string} jobId
   * @param {"json"|"csv"} format
   */
  async downloadResults(jobId, format = 'json') {
    const response = await this.httpClient.get(`/batch-jobs/${jobId}/results`, {
      params: { format },
      responseType: 'blob',
      timeout: 300000, // large result sets stream slowly; allow up to 5 minutes
    });

    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `batch-job-${jobId}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

const batchJobsService = new BatchJobsService();
export default batchJobsService;
